import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useWindowStore } from './store';
import css from './Window.module.css';
import type { Rect, ResizeDir, WindowState } from './types';

const DIRECTIONS: readonly ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const HANDLE_CLASS: Record<ResizeDir, string> = {
  n: css.handleN,
  s: css.handleS,
  e: css.handleE,
  w: css.handleW,
  ne: css.handleNe,
  nw: css.handleNw,
  se: css.handleSe,
  sw: css.handleSw,
};

/** Kept in sync with the cursors in `Window.module.css`. */
const CURSOR: Record<ResizeDir, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

interface ResizeSession {
  pointerId: number;
  el: HTMLElement;
  dir: ResizeDir;
  startX: number;
  startY: number;
  /** Geometry at grab time; every frame is a delta from this, never cumulative. */
  rect: Rect;
  prevUserSelect: string;
  prevCursor: string;
  detach: () => void;
}

export interface ResizeHandleProps {
  className: string;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}

export interface ResizeHandlers {
  handleProps: (dir: ResizeDir) => ResizeHandleProps;
}

/**
 * Edge and corner resizing, one pointer-captured session at a time.
 *
 * Minimum size and the "the title bar may not leave the screen" rule are the
 * store's business (`placement.resizeRect`); this hook only reports deltas.
 */
export function useResize(win: WindowState): ResizeHandlers {
  const winRef = useRef(win);
  const sessionRef = useRef<ResizeSession | null>(null);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<{ dx: number; dy: number } | null>(null);

  useEffect(() => {
    winRef.current = win;
  });

  const flush = useCallback(() => {
    frameRef.current = null;
    const session = sessionRef.current;
    const pending = pendingRef.current;
    if (!session || !pending) return;
    pendingRef.current = null;
    useWindowStore
      .getState()
      .resizeBy(winRef.current.id, session.dir, session.rect, pending.dx, pending.dy);
  }, []);

  const finish = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;

    session.detach();

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    // Flush before clearing the session — `flush` reads the direction from it.
    flush();
    sessionRef.current = null;

    try {
      if (session.el.hasPointerCapture(session.pointerId)) {
        session.el.releasePointerCapture(session.pointerId);
      }
    } catch {
      // The handle can already be gone if the window closed mid-resize.
    }

    document.body.style.userSelect = session.prevUserSelect;
    document.body.style.cursor = session.prevCursor;
    useWindowStore.getState().setInteracting(false);
  }, [flush]);

  const begin = useCallback(
    (dir: ResizeDir, event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      if (sessionRef.current) return;

      const current = winRef.current;
      if (!current.resizable || current.maximized) return;

      const el = event.currentTarget;
      event.preventDefault();
      event.stopPropagation();

      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        // Capture is an optimisation; the listeners below still work without it.
      }

      const handleMove = (e: PointerEvent) => {
        const session = sessionRef.current;
        if (!session || e.pointerId !== session.pointerId) return;
        pendingRef.current = { dx: e.clientX - session.startX, dy: e.clientY - session.startY };
        if (frameRef.current === null) frameRef.current = requestAnimationFrame(flush);
      };

      const handleEnd = (e: PointerEvent) => {
        const session = sessionRef.current;
        if (session && e.pointerId !== session.pointerId) return;
        finish();
      };

      const detach = () => {
        el.removeEventListener('pointermove', handleMove);
        el.removeEventListener('pointerup', handleEnd);
        el.removeEventListener('pointercancel', handleEnd);
        el.removeEventListener('lostpointercapture', handleEnd);
      };

      el.addEventListener('pointermove', handleMove);
      el.addEventListener('pointerup', handleEnd);
      el.addEventListener('pointercancel', handleEnd);
      el.addEventListener('lostpointercapture', handleEnd);

      sessionRef.current = {
        pointerId: event.pointerId,
        el,
        dir,
        startX: event.clientX,
        startY: event.clientY,
        rect: { x: current.x, y: current.y, w: current.w, h: current.h },
        prevUserSelect: document.body.style.userSelect,
        prevCursor: document.body.style.cursor,
        detach,
      };

      document.body.style.userSelect = 'none';
      // The shield inherits this, so the cursor stays put even when the
      // pointer wanders off the 6px handle mid-drag.
      document.body.style.cursor = CURSOR[dir];
      useWindowStore.getState().setInteracting(true);
    },
    [finish, flush],
  );

  const handlers = useMemo(() => {
    const map = {} as Record<ResizeDir, ResizeHandleProps>;
    for (const dir of DIRECTIONS) {
      map[dir] = {
        className: `${css.handle} ${HANDLE_CLASS[dir]}`,
        onPointerDown: (event: ReactPointerEvent<HTMLElement>) => begin(dir, event),
      };
    }
    return map;
  }, [begin]);

  const handleProps = useCallback((dir: ResizeDir) => handlers[dir], [handlers]);

  useEffect(() => finish, [finish]);

  return { handleProps };
}
